import type { HTMLAttributes, ReactNode } from 'react';
import { AlertTriangle, Copy, GripVertical, Trash2 } from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { ProjectoPLItem } from '../../types/projecto';
import { PROJECTO_ITEM_TYPE_LABELS } from '../../types/projecto';
import { getItemCostRows, getItemFigures, commissionPctOf } from '../../services/projectoService';
import { formatEuro } from '../../utils/currency';
import { StaticRow, type RowTone } from '../ui/ListRows';

/**
 * - billed: a supplier line on "costs + commission": cost, commission, charged;
 * - cost: only what the agency pays (fixed budget, internal costs);
 * - service: the agency's own work, only what is charged.
 */
export type PLRowVariant = 'billed' | 'cost' | 'service';

export const PL_GRID: Record<PLRowVariant, string> = {
  billed: 'sm:grid-cols-[minmax(0,1fr)_6.5rem_6.5rem_6.5rem_5.5rem_5.25rem]',
  cost: 'sm:grid-cols-[minmax(0,1fr)_7rem_5.5rem_5.25rem]',
  service: 'sm:grid-cols-[minmax(0,1fr)_7rem_5.25rem]',
};

/** Paid means the suppliers on this line, not the client (that's the Faturas tab). */
export function paidState(item: ProjectoPLItem): { label: string; tone: RowTone; chip: string } | null {
  const rows = getItemCostRows(item).filter((c) => Number(c?.amount) > 0);
  if (rows.length === 0) return null;
  const paid = rows.filter((c) => c.paid).length;
  if (paid === rows.length) return { label: 'Pago', tone: 'success', chip: 'cm-chip-success' };
  if (paid > 0) return { label: `${paid} de ${rows.length}`, tone: 'info', chip: 'cm-chip-info' };
  return { label: 'Por pagar', tone: 'neutral', chip: 'cm-chip-neutral' };
}

function Money({ label, children, className = '' }: { label: string; children: ReactNode; className?: string }) {
  return (
    <div className={`min-w-0 sm:text-right ${className}`}>
      <p className="sm:hidden text-[11px] text-gray-500">{label}</p>
      <div className="text-sm tabular-nums">{children}</div>
    </div>
  );
}

interface Props {
  item: ProjectoPLItem;
  variant: PLRowVariant;
  onOpen: (item: ProjectoPLItem) => void;
  onDuplicate: (item: ProjectoPLItem) => void;
  onDelete: (item: ProjectoPLItem) => void;
}

export function ProjectoPLRow({ item, variant, onOpen, onDuplicate, onDelete }: Props) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  const { cost, charge, margin, missingCost } = getItemFigures(item);
  const missing = missingCost && variant === 'billed';
  const paid = paidState(item);
  const tone: RowTone = missing ? 'warning' : paid?.tone ?? 'neutral';

  const who = getItemCostRows(item).map((c) => c.label?.trim()).filter(Boolean).join(', ') || item.vendor_name;
  const quantity = Number(item.quantity) || 1;
  const units = variant === 'service' && quantity > 1 ? `${quantity} × ${formatEuro(Number(item.unit_price) || 0)}` : null;
  const pct = commissionPctOf(cost, charge);

  const paidChip = paid && <span className={`cm-chip ${paid.chip} whitespace-nowrap`}>{paid.label}</span>;
  const dragHandle: HTMLAttributes<HTMLButtonElement> = { ...attributes, ...listeners };

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`relative ${isDragging ? 'z-10 opacity-60 shadow-lg ring-2 ring-accent-400 rounded-xl bg-white' : ''}`}
    >
      <StaticRow tone={tone}>
        <div
          role="button"
          tabIndex={0}
          aria-label={`Abrir ${item.name}`}
          onClick={() => onOpen(item)}
          onKeyDown={(e) => {
            if (e.target !== e.currentTarget) return;
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(item); }
          }}
          className={`grid grid-cols-[minmax(0,1fr)_auto] ${PL_GRID[variant]} items-center gap-x-3 gap-y-1.5 rounded-lg -my-1 py-1 pl-1 cursor-pointer hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-ink/30`}
        >
          <div className="min-w-0 order-1 sm:order-none">
            <p className="text-sm font-medium text-gray-900 truncate">{item.name}</p>
            <p className="flex flex-wrap items-center gap-x-1.5 text-xs text-gray-500">
              <span>{PROJECTO_ITEM_TYPE_LABELS[item.item_type] ?? item.item_type}</span>
              {who && <span className="truncate max-w-full">· {who}</span>}
              {units && <span className="whitespace-nowrap">· {units}</span>}
              {paidChip && <span className="sm:hidden">{paidChip}</span>}
            </p>
          </div>

          <div className="order-3 col-span-2 grid grid-cols-3 gap-x-2 sm:contents">
            {variant === 'billed' && (
              <>
                <Money label="Custo">
                  {missing ? (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-800 whitespace-nowrap">
                      <AlertTriangle className="w-3.5 h-3.5" /> Falta custo
                    </span>
                  ) : <span className="text-gray-700">{formatEuro(cost)}</span>}
                </Money>
                <Money label="Comissão">
                  {missing ? <span className="text-gray-400">—</span> : (
                    <>
                      <span className={`font-medium ${margin < 0 ? 'text-red-700' : margin > 0 ? 'text-emerald-700' : 'text-gray-500'}`}>{formatEuro(margin)}</span>
                      {cost > 0 && <span className="block text-[11px] text-gray-500">{pct.toLocaleString('pt-PT', { maximumFractionDigits: 1 })}%</span>}
                    </>
                  )}
                </Money>
                <Money label="Cobrado"><span className="font-semibold text-gray-900">{formatEuro(charge)}</span></Money>
              </>
            )}
            {variant === 'cost' && <Money label="Custo"><span className="font-semibold text-gray-900">{formatEuro(cost)}</span></Money>}
            {variant === 'service' && <Money label="Valor"><span className="font-semibold text-gray-900">{formatEuro(charge)}</span></Money>}
          </div>

          {variant !== 'service' && <div className="hidden sm:block text-right">{paidChip}</div>}

          <div className="order-2 sm:order-last flex items-center justify-end" onClick={(e) => e.stopPropagation()}>
            <button type="button" onClick={() => onDuplicate(item)} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100" aria-label={`Duplicar ${item.name}`} title="Duplicar">
              <Copy className="w-3.5 h-3.5" />
            </button>
            <button type="button" onClick={() => onDelete(item)} className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50" aria-label={`Eliminar ${item.name}`} title="Eliminar">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
            <button type="button" {...dragHandle} className="hidden sm:block p-1 rounded-lg text-gray-300 hover:text-gray-600 hover:bg-gray-100 cursor-grab active:cursor-grabbing touch-none" aria-label={`Arrastar ${item.name}`}>
              <GripVertical className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </StaticRow>
    </div>
  );
}
